import { useMemo, memo } from 'react';
import { Send, Mail, Bell } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { EmailEditorPanel, type EmailDoc, type EmailEditorValue } from '@/components/email-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { NotificationChannel } from '@masters-club/types';
import type { BroadcastMedia, BroadcastButton } from '../types';
import { LanguageSwitcher, LANGUAGE_META } from '@/components/translations';
import type { TranslationLanguage, TranslationFieldConfig, TranslationData } from '@/components/translations';
import { PARSE_MODE_OPTIONS } from './constants';
import { ChannelSelector } from './channel-selector';
import { TelegramMediaSection, ButtonsSection } from './channel-content-sections';

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
};

/** Единственное поле для LanguageSwitcher — messageTemplate */
const BROADCAST_FIELDS: TranslationFieldConfig[] = [
  { name: 'messageTemplate', label: 'Содержание', type: 'textarea', required: true },
];

interface TabMessageProps {
  name: string;
  setName: (value: string) => void;
  messageTemplate: string;
  setMessageTemplate: (value: string) => void;
  parseMode: 'Markdown' | 'HTML' | 'none';
  setParseMode: (value: 'Markdown' | 'HTML' | 'none') => void;
  channels: NotificationChannel[];
  toggleChannel: (checked: boolean, channel: NotificationChannel) => void;
  hasChannels: boolean;
  availableVariables: string[];
  isPending: boolean;
  languages: LanguageRecord[];
  translations: Map<string, string>;
  activeTranslationTab: string;
  setActiveTranslationTab: (value: string) => void;
  updateTranslation: (langCode: string, template: string) => void;
  // Email visual editor
  emailVisualEnabled: boolean;
  setEmailVisualEnabled: (value: boolean) => void;
  emailDoc: EmailDoc | undefined;
  onEmailEditorChange: (next: EmailEditorValue) => void;
  // Telegram media
  mediaEnabled: boolean;
  setMediaEnabled: (value: boolean) => void;
  mediaType: BroadcastMedia['type'];
  setMediaType: (value: BroadcastMedia['type']) => void;
  mediaUrl: string;
  setMediaUrl: (value: string) => void;
  mediaCaption: string;
  setMediaCaption: (value: string) => void;
  mediaParseMode: 'Markdown' | 'HTML' | 'none';
  setMediaParseMode: (value: 'Markdown' | 'HTML' | 'none') => void;
  // Buttons
  buttons: BroadcastButton[];
  addButton: () => void;
  updateButton: (index: number, next: Partial<BroadcastButton>) => void;
  removeButton: (index: number) => void;
  emailButtons: BroadcastButton[];
  addEmailButton: () => void;
  updateEmailButton: (index: number, next: Partial<BroadcastButton>) => void;
  removeEmailButton: (index: number) => void;
  appButtons: BroadcastButton[];
  addAppButton: () => void;
  updateAppButton: (index: number, next: Partial<BroadcastButton>) => void;
  removeAppButton: (index: number) => void;
}

export const TabMessage = memo(function TabMessage({
  name,
  setName,
  messageTemplate,
  setMessageTemplate,
  parseMode,
  setParseMode,
  channels,
  toggleChannel,
  hasChannels,
  availableVariables,
  isPending,
  languages,
  translations,
  activeTranslationTab,
  setActiveTranslationTab,
  updateTranslation,
  emailVisualEnabled,
  setEmailVisualEnabled,
  emailDoc,
  onEmailEditorChange,
  mediaEnabled,
  setMediaEnabled,
  mediaType,
  setMediaType,
  mediaUrl,
  setMediaUrl,
  mediaCaption,
  setMediaCaption,
  mediaParseMode,
  setMediaParseMode,
  buttons,
  addButton,
  updateButton,
  removeButton,
  emailButtons,
  addEmailButton,
  updateEmailButton,
  removeEmailButton,
  appButtons,
  addAppButton,
  updateAppButton,
  removeAppButton,
}: TabMessageProps) {
  const hasTelegram = channels.includes('TELEGRAM');
  const hasEmail = channels.includes('EMAIL');
  const hasApp = channels.includes('APP');
  const defaultLangCode = languages[0]?.code ?? 'ru';

  const activeLangCode = activeTranslationTab === 'default'
    ? defaultLangCode
    : activeTranslationTab;

  const switcherLanguages: TranslationLanguage[] = useMemo(() =>
    languages.map((lang) => ({
      code: lang.code,
      name: lang.name,
      nativeName: lang.nativeName,
      flag: LANGUAGE_META[lang.code]?.flag ?? '🌐',
    })),
    [languages],
  );

  const translationData: TranslationData = useMemo(() => {
    const data: TranslationData = {};
    for (const lang of languages) {
      const text = lang.code === defaultLangCode
        ? messageTemplate
        : (translations.get(lang.code) ?? '');
      data[lang.code] = { messageTemplate: text };
    }
    return data;
  }, [languages, defaultLangCode, messageTemplate, translations]);

  const currentText = activeLangCode === defaultLangCode
    ? messageTemplate
    : (translations.get(activeLangCode) ?? '');

  const handleTextChange = (value: string) => {
    if (activeLangCode === defaultLangCode) {
      setMessageTemplate(value);
    } else {
      updateTranslation(activeLangCode, value);
    }
  };

  const activeLang = languages.find((l) => l.code === activeLangCode);

  return (
    <div className="space-y-8">
      {/* ─── Шаг 1: Каналы ─────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Куда отправляем</h3>
          <p className="text-xs text-muted-foreground">
            Выберите один или несколько каналов доставки
          </p>
        </div>
        <ChannelSelector
          channels={channels}
          toggleChannel={toggleChannel}
          disabled={isPending}
        />
        {!hasChannels && (
          <p className="text-xs text-destructive">Выберите хотя бы один канал</p>
        )}
      </section>

      {/* ─── Шаг 2: Основное ───────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="broadcast-name">Название рассылки (для админки)</Label>
          <Input
            id="broadcast-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Новогодняя акция..."
            disabled={isPending}
          />
        </div>

        <div className="space-y-2 md:max-w-sm">
          <Label>Режим разметки текста</Label>
          <Select
            value={parseMode}
            onValueChange={(value) =>
              setParseMode(value as 'Markdown' | 'HTML' | 'none')
            }
            disabled={isPending}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите режим" />
            </SelectTrigger>
            <SelectContent>
              {PARSE_MODE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="shrink-0">Текст сообщения</Label>
            {languages.length > 0 && (
              <LanguageSwitcher
                languages={switcherLanguages}
                activeLanguage={activeLangCode}
                onLanguageChange={setActiveTranslationTab}
                fields={BROADCAST_FIELDS}
                value={translationData}
                variant="inline"
              />
            )}
          </div>

          <Textarea
            value={currentText}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={`Содержание на ${activeLang?.nativeName ?? activeLangCode}...`}
            className="min-h-[200px] font-mono text-sm"
            disabled={isPending}
          />

          {activeLangCode !== defaultLangCode && !currentText.trim() && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Перевод на «{activeLang?.nativeName}» не заполнен — получатели с этим языком не получат рассылку.
            </p>
          )}

          <VariablesHint variables={availableVariables} />
        </div>
      </section>

      {/* ─── Шаг 3: Настройки по каналам ───────────────────────────────── */}
      {hasTelegram && (
        <ChannelBlock icon={<Send className="h-4 w-4" />} title="Telegram">
          <TelegramMediaSection
            mediaEnabled={mediaEnabled}
            setMediaEnabled={setMediaEnabled}
            mediaType={mediaType}
            setMediaType={setMediaType}
            mediaUrl={mediaUrl}
            setMediaUrl={setMediaUrl}
            mediaCaption={mediaCaption}
            setMediaCaption={setMediaCaption}
            mediaParseMode={mediaParseMode}
            setMediaParseMode={setMediaParseMode}
            isPending={isPending}
          />
          <div className="border-t pt-4">
            <ButtonsSection
              title="Кнопки под сообщением"
              hint="Можно использовать переменные в тексте и ссылке"
              buttons={buttons}
              onAdd={addButton}
              onUpdate={updateButton}
              onRemove={removeButton}
              isPending={isPending}
              showCallbackData
            />
          </div>
        </ChannelBlock>
      )}

      {hasEmail && (
        <ChannelBlock icon={<Mail className="h-4 w-4" />} title="Эл. почта">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-0.5">
              <Label htmlFor="email-visual-toggle">Визуальное HTML-письмо</Label>
              <p className="text-xs text-muted-foreground">
                Соберите письмо в редакторе. Если выключено — отправляется
                текст из «Текст сообщения».
              </p>
            </div>
            <Switch
              id="email-visual-toggle"
              checked={emailVisualEnabled}
              onCheckedChange={setEmailVisualEnabled}
              disabled={isPending}
            />
          </div>

          {emailVisualEnabled && (
            <EmailEditorPanel
              key={emailVisualEnabled ? 'email-visual-on' : 'email-visual-off'}
              value={emailDoc}
              onChange={onEmailEditorChange}
              placeholders={availableVariables}
              readOnly={isPending}
            />
          )}

          <div className="border-t pt-4">
            <ButtonsSection
              title="CTA-кнопки в письме"
              hint="Текст + ссылка"
              buttons={emailButtons}
              onAdd={addEmailButton}
              onUpdate={updateEmailButton}
              onRemove={removeEmailButton}
              isPending={isPending}
            />
          </div>
        </ChannelBlock>
      )}

      {hasApp && (
        <ChannelBlock icon={<Bell className="h-4 w-4" />} title="Уведомление в приложении">
          <ButtonsSection
            title="Кнопки в push-уведомлении"
            hint="Текст + ссылка"
            buttons={appButtons}
            onAdd={addAppButton}
            onUpdate={updateAppButton}
            onRemove={removeAppButton}
            isPending={isPending}
          />
        </ChannelBlock>
      )}
    </div>
  );
});

// ─── Channel block wrapper ───────────────────────────────────────────────────

const ChannelBlock = memo(function ChannelBlock({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span className="text-muted-foreground">{icon}</span>
        {title}
      </div>
      {children}
    </section>
  );
});

// ─── Переиспользуемый компонент переменных ───────────────────────────────────

interface VariablesHintProps {
  variables: string[];
}

const VariablesHint = memo(function VariablesHint({ variables }: VariablesHintProps) {
  if (variables.length === 0) { return null; }

  const handleCopy = (variable: string) => {
    navigator.clipboard.writeText(variable);
  };

  return (
    <div className="bg-muted/50 rounded-lg p-3 text-sm">
      <p className="text-xs font-medium text-muted-foreground mb-2">Доступные переменные:</p>
      <div className="flex flex-wrap gap-1.5">
        {variables.map((variable) => (
          <code
            key={variable}
            className="bg-background px-2 py-0.5 rounded text-xs cursor-pointer hover:bg-accent transition-colors"
            onClick={() => handleCopy(variable)}
            title="Нажмите для копирования"
          >
            {variable}
          </code>
        ))}
      </div>
    </div>
  );
});
